import React, { useEffect, useState } from 'react';
import LayoutForm from 'automaailm-ui/lib/Layouts/LayoutForm';
import FormRow from 'automaailm-ui/lib/Components/FormRow';
import FormRowGroup from 'automaailm-ui/lib/Components/FormRowGroup';
import ControlInput from 'automaailm-ui/lib/Components/ControlInput';
import ControlSelect from 'automaailm-ui/lib/Components/ControlSelect';
import Button from 'automaailm-ui/lib/Components/Button';
import Buttons from 'automaailm-ui/lib/Components/Buttons';
import Callout from 'automaailm-ui/lib/Components/Callout';
import { useFormik } from 'formik';
import loadOrderRequest, { IOrderCredentials } from 'data/requests/orderReturns/orderLoaderRequest';
import { IReturnOrder, ITranslations } from 'components/OrderReturns/ReturnForm';
import { identificationRules } from 'components/OrderReturns/ValidationRules';
import { splitPhone } from 'components/OrderReturns/splitPhone';
import { useSelector } from 'react-redux';
import { useRequest } from 'redux-query-react';
import { getParamFromURL } from 'components/Checkout/helpers/getParamFromURL';
import { getIsLoggedIn } from 'hooks/useAuth';
import { getCaptchaToken } from 'components/GoogleRecaptcha/Index';

interface IProps {
    labels: ITranslations;
    defaultPhonePrefix: string;
    phonePrefixes: string[];
}

const mapStateToProps = (state) => {
    return {
        order: state.entities.orderReturn as IReturnOrder,
        loadError: state.entities.orderReturnError as string,
    };
};

const OrderLoadForm = (props: IProps) => {
    const { labels, defaultPhonePrefix, phonePrefixes } = props;
    const { order, loadError } = useSelector(mapStateToProps);
    const isLoggedIn = getIsLoggedIn();
    const [credentials, setCredentials] = useState<IOrderCredentials>();
    const [{ isPending, isFinished }] = useRequest(credentials ? loadOrderRequest(credentials) : null);

    const orderNumberQuery = getParamFromURL(window.location.search, 'orderNumber') ?? '';

    const identificationForm = useFormik({
        initialValues: { orderNumber: orderNumberQuery, email: '', phoneArea: defaultPhonePrefix, phone: '' },
        validationSchema: identificationRules(labels),
        validateOnChange: false,
        validateOnBlur: false,
        onSubmit: async (values) => {
            const captchaToken = await getCaptchaToken().catch(() => undefined);
            setCredentials({
                orderNumber: values.orderNumber,
                email: values.email,
                phone: values.phoneArea + values.phone,
                captchaToken,
            });
        },
    });

    useEffect(() => {
        if (!isLoggedIn || !orderNumberQuery) {
            return;
        }

        void getCaptchaToken()
            .catch(() => undefined)
            .then((captchaToken) => setCredentials({ orderNumber: orderNumberQuery, captchaToken }));
    }, [isLoggedIn, orderNumberQuery]);

    useEffect(() => {
        if (!order) {
            return;
        }

        identificationForm.setValues({
            orderNumber: order.orderNumber,
            email: order.customer.email,
            ...splitPhone(order.customer.phone, phonePrefixes, defaultPhonePrefix),
        });
    }, [order?.orderNumber]);

    const { orderNumber, email, phone } = identificationForm.values;
    const canSubmit = Boolean(orderNumber && email && phone);

    const loadFailed = Boolean(credentials) && isFinished && !isPending && !order;

    return (
        <React.Fragment>
            <p>{labels.explanation}</p>

            {loadFailed && <Callout intent="danger">{loadError ?? labels.errorLoadFailed}</Callout>}

            <form onSubmit={identificationForm.handleSubmit}>
                <LayoutForm layout="vertical">
                    <FormRow label={labels.orderNumber} required={true} error={identificationForm.errors.orderNumber}>
                        <ControlInput
                            name="orderNumber"
                            value={identificationForm.values.orderNumber}
                            disabled={!!order}
                            hasError={!!identificationForm.errors.orderNumber}
                            onChange={identificationForm.handleChange}
                            onBlur={identificationForm.handleBlur}
                        />
                    </FormRow>
                    <FormRow label={labels.email} required={true} error={identificationForm.errors.email}>
                        <ControlInput
                            name="email"
                            value={identificationForm.values.email}
                            disabled={!!order}
                            hasError={!!identificationForm.errors.email}
                            onChange={identificationForm.handleChange}
                            onBlur={identificationForm.handleBlur}
                        />
                    </FormRow>
                    <FormRow label={labels.phone} required={true} error={identificationForm.errors.phone}>
                        <FormRowGroup>
                            <ControlSelect
                                name="phoneArea"
                                value={identificationForm.values.phoneArea}
                                disabled={!!order}
                                size="small"
                                onChange={identificationForm.handleChange}
                            >
                                {phonePrefixes.map((prefix) => (
                                    <option key={prefix} value={prefix}>
                                        {prefix}
                                    </option>
                                ))}
                            </ControlSelect>
                            <ControlInput
                                name="phone"
                                value={identificationForm.values.phone}
                                disabled={!!order}
                                hasError={!!identificationForm.errors.phone}
                                onChange={identificationForm.handleChange}
                                onBlur={identificationForm.handleBlur}
                            />
                        </FormRowGroup>
                    </FormRow>
                    {!order && (
                        <Buttons layout="vertical-wide">
                            <Button
                                type="submit"
                                title={labels.loadOrder}
                                loading={isPending}
                                disabled={isPending || !canSubmit}
                                intent="primary"
                            />
                        </Buttons>
                    )}
                </LayoutForm>
            </form>
        </React.Fragment>
    );
};

export default OrderLoadForm;
